import { authGuard, permissionGuard } from '@abp/ng.core';
import { Routes } from '@angular/router';
import { firstAllowedRouteGuard } from 'src/core/guards/first-allowed-route.guard';
import { PermissionConstants } from 'src/core/constants/permission.constant';
import { activeCallDeactivateGuard } from './shared/agent-desktop/guards/active-call-deactivate.guard';
import { IDENTITY_EDIT_FORM_PROP_CONTRIBUTORS, IDENTITY_ENTITY_PROP_CONTRIBUTORS, IDENTITY_FORM_PROP_CONTRIBUTORS } from './administration/identity/providers/identity-contributors';
import { SAAS_ENTITY_PROP_CONTRIBUTORS } from './administration/saas/providers/saas-contributors';
import { hostBlockedGuard } from 'src/core/guards/host-blocked.guard';

export const APP_ROUTES: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [firstAllowedRouteGuard],
    loadComponent: () => import('./home/home.component').then(c => c.HomeComponent),
  },
  {
    path: 'no-access',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./no-access/no-access.component').then(c => c.NoAccessComponent),
  },
  {
    path: 'dashboard',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: PermissionConstants.canViewTenantDashboard },
    loadComponent: () =>
      import('./features/dashboard/components/tenant-dashboard/tenant-dashboard.component').then(
        c => c.TenantDashboardComponent,
      ),
  },
  {
    path: 'notifications',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/notifications/components/notification-main/notification-main.component').then(
        c => c.NotificationMainComponent,
      ),
  },
  {
    path: 'agent-desktop',
    canActivate: [authGuard, permissionGuard],
    canDeactivate: [activeCallDeactivateGuard],
    data: { requiredPolicy: PermissionConstants.canViewAgentDesktop },
    loadComponent: () =>
      import('./shared/agent-desktop/agent-desktop.component').then(c => c.AgentDesktopComponent),
  },
  {
    path: 'contacts/:id',
    canActivate: [authGuard, permissionGuard],
    loadComponent: () =>
      import('./shared/agent-desktop/components/contact-detail/contact-detail.component').then(
        c => c.ContactDetailComponent,
      ),
  },
  {
    path: 'contacts',
    canActivate: [authGuard, permissionGuard],
    // data: { requiredPolicy: PermissionConstants.canViewContacts },
    loadComponent: () =>
      import('./features/contact/components/contact-main/contact-main.component').then(
        c => c.ContactMainComponent,
      ),
  },
  {
    path: 'conversations',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: PermissionConstants.canViewConversations },
    loadComponent: () =>
      import('./features/conversation/components/conversation-main/conversation-main.component').then(
        c => c.ConversationMainComponent,
      ),
  },
  {
    path: 'campaigns',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: PermissionConstants.canViewCampaigns },
    loadChildren: () =>
      import('./features/campaigns/campaigns.routes').then(c => c.CAMPAIGNS_ROUTES),
  },
  {
    path: 'administration/agent-management',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: PermissionConstants.agentManagement },
    loadComponent: () =>
      import('./features/agent-management/components/agent-main/agent-main.component').then(
        c => c.AgentMainComponent,
      ),
  },
  {
    path: 'account',
    loadChildren: () => import('@volo/abp.ng.account/public').then(c => c.createRoutes()),
  },
  {
    path: 'gdpr',
    loadChildren: () => import('@volo/abp.ng.gdpr').then(c => c.createRoutes()),
  },
  {
    // Removed from the product for both host and tenant users; kept as a redirect rather than deleting
    // the library-contributed 'organization-units' child route, matched before it so direct URLs bounce home.
    path: 'identity/organization-units',
    redirectTo: '/',
    pathMatch: 'full',
  },
  {
    path: 'chat',
    loadChildren: () => import('@volo/abp.ng.chat').then(c => c.createRoutes()),
  },
  {
    path: 'identity',
    loadChildren: () =>
      import('@volo/abp.ng.identity').then(c =>
        c.createRoutes({
          entityPropContributors: IDENTITY_ENTITY_PROP_CONTRIBUTORS,
          createFormPropContributors: IDENTITY_FORM_PROP_CONTRIBUTORS,
          editFormPropContributors: IDENTITY_EDIT_FORM_PROP_CONTRIBUTORS,
        }),
      ),
  },
  {
    path: 'language-management',
    loadChildren: () => import('@volo/abp.ng.language-management').then(c => c.createRoutes()),
  },
  {
    path: 'saas',
    loadChildren: () =>
      import('@volo/abp.ng.saas').then(c =>
        c.createRoutes({
          entityPropContributors: SAAS_ENTITY_PROP_CONTRIBUTORS,
        }),
      ),
  },
  {
    path: 'audit-logs',
    loadChildren: () => import('@volo/abp.ng.audit-logging').then(c => c.createRoutes()),
  },
  {
    path: 'openiddict',
    loadChildren: () => import('@volo/abp.ng.openiddictpro').then(c => c.createRoutes()),
  },
  {
    path: 'text-template-management',
    loadChildren: () => import('@volo/abp.ng.text-template-management').then(c => c.createRoutes()),
  },
  {
    path: 'setting-management',
    loadChildren: () => import('@abp/ng.setting-management').then(c => c.createRoutes()),
  },
  {
    path: 'phone-numbers',
    canActivate: [hostBlockedGuard],
    loadChildren: () =>
      import('./administration/settings/phone-numbers/components/phone-numbers-main/phone-numbers-main.component').then(
        c => c.PhoneNumbersMainComponent,
      ),
  },
  {
    path: 'gdpr-cookie-consent',
    children: [
      {
        path: 'privacy',
        loadComponent: () =>
          import('./gdpr-cookie-consent/privacy-policy.component').then(
            c => c.PrivacyPolicyComponent,
          ),
      },
      {
        path: 'cookie',
        loadComponent: () =>
          import('./gdpr-cookie-consent/cookie-policy.component').then(
            c => c.CookiePolicyComponent,
          ),
      },
    ],
  },
];
